import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthScreen } from './AuthScreen';
import { callApi } from '../../lib/api';
import { errorMessage } from '../../lib/query';
import { rememberNext, useAuth } from '../../lib/auth';

/**
 * Magic-link sign-in. Posts the email to `/auth/start`, which always answers OK and only sends
 * a link to allowlisted emails, so this screen never reveals who is a member.
 */
export default function Login() {
  const { session } = useAuth();
  const from = (useLocation().state as { from?: string } | null)?.from ?? null;
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (session === undefined) return null;
  if (session) return <Navigate to={from ?? '/'} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setError('Enter a full email address, like name@company.com.');
      return;
    }
    setError(null);
    setState('sending');
    try {
      rememberNext(from);
      await callApi('/auth/start', { email: clean, redirect_to: `${location.origin}/auth/callback` });
      setEmail(clean);
      setState('sent');
    } catch (err) {
      setState('idle');
      setError(errorMessage(err));
    }
  };

  if (state === 'sent') {
    return (
      <AuthScreen title="Check your email">
        <p>
          If <b>{email}</b> is on the team list, a sign-in link is on its way. It works once and expires in an hour.
        </p>
        <p className="mut">No email after a few minutes? Check spam, or ask the owner to add this address in Settings.</p>
        <button type="button" className="btn" onClick={() => setState('idle')}>
          Use a different email
        </button>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title="Sign in">
      <p>We email you a one-time sign-in link. There is no password.</p>
      <form onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="email">Work email</label>
          <input
            id="email"
            className="input"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!error}
            aria-describedby={error ? 'email-err' : undefined}
            autoFocus
          />
          {error ? (
            <small id="email-err" className="b-n" role="alert">
              {error}
            </small>
          ) : null}
        </div>
        <button type="submit" className="btn primary" disabled={state === 'sending'}>
          {state === 'sending' ? 'Sending…' : 'Send sign-in link'}
        </button>
      </form>
      <p className="foot-note">Only people the owner has added can sign in.</p>
    </AuthScreen>
  );
}
